import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import { Markdown } from "tiptap-markdown";
import { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Code,
  Code2,
  Smile,
  AtSign,
} from "lucide-react";
import { nameInitials } from "../../lib/nameInitials";

const EMOJI_SET = [
  "👍","❤️","🎉","🚀","✅","👀","🙌","🔥","😀","😂","😊","😍",
  "🤔","😅","🙏","👏","💯","👌","🤝","👋","😎","😢","😡","🥳",
  "💪","🎯","💡","⭐","☕","🍕","🐛","📝",
];

/**
 * True WYSIWYG rich-text editor built on Tiptap.
 * - Toolbar buttons apply real formatting (visible bold/italic/etc.)
 * - `@` triggers a Slack-style user picker; picked users become a mention
 *   node (rendered as blue chip) and their id is emitted via `onMention`.
 * - Emits **markdown** via `tiptap-markdown` so the value round-trips
 *   through the existing plain-text `description` / comment `body` fields.
 *   Mentions serialize back as `@localpart` so the backend authoritative
 *   parser can turn them into assignees.
 */
export default function RichTextEditor({
  value,
  onChange,
  users = [],
  onMention,
  placeholder,
  rows = 3,
  className = "",
  bordered = true,
  onSubmit,
  autoFocus = false,
  readOnly = false,
  "data-testid": testId,
}) {
  const [suggestion, setSuggestion] = useState(null); // { items, clientRect, index }
  const suggestionRef = useRef(null); // holds current props from tiptap (command, items, clientRect)
  const usersRef = useRef(users);
  const onMentionRef = useRef(onMention);
  useEffect(() => { usersRef.current = users; }, [users]);
  useEffect(() => { onMentionRef.current = onMention; }, [onMention]);

  const pickMention = (user) => {
    const props = suggestionRef.current;
    if (!props || !user) return;
    const label = (user.email || "").split("@")[0] || user.name;
    props.command({ id: user.id, label });
    onMentionRef.current?.(user.id);
    setSuggestion(null);
    suggestionRef.current = null;
  };

  const mentionExtension = useMemo(
    () =>
      Mention.extend({
        // Teach tiptap-markdown how to serialize this node so it round-trips
        // as `@localpart` in markdown, which the backend authoritative parser
        // resolves back to an assignee.
        addStorage() {
          return {
            markdown: {
              serialize(state, node) {
                state.write(`@${node.attrs.label || node.attrs.id}`);
              },
              parse: {},
            },
          };
        },
      }).configure({
        HTMLAttributes: {
          class: "mention rounded bg-blue-50 px-1 text-blue-700 font-medium",
        },
        renderText({ node }) {
          return `@${node.attrs.label || node.attrs.id}`;
        },
        suggestion: {
          char: "@",
          allowSpaces: false,
          items: ({ query }) => {
            const q = (query || "").toLocaleLowerCase("tr");
            // Prefix match: query starts a token in the user's name/email.
            // Tokens = whole name + each space-separated word + email
            // localpart. Empty query returns everyone. Case + Turkish
            // diacritic aware.
            const matches = (u) => {
              if (!q) return true;
              const name = (u.name || "").toLocaleLowerCase("tr");
              const email = (u.email || "").toLocaleLowerCase("tr");
              const localpart = email.split("@")[0] || "";
              if (name.startsWith(q)) return true;
              if (localpart.startsWith(q)) return true;
              // Word-boundary prefix — matches surname / middle name too.
              for (const part of name.split(/\s+/)) {
                if (part.startsWith(q)) return true;
              }
              return false;
            };
            return usersRef.current
              .filter((u) => u.active !== false)
              .filter(matches)
              // Sort by display name (Turkish collation) so the list order
              // is stable. Falls back to email localpart when a user has
              // no `name`. No slice — dropdown is scrollable (`max-h-56
              // overflow-y-auto`) so 500-user tenants still work.
              .sort((a, b) => {
                const aKey = (a.name || (a.email || "").split("@")[0] || "").toLocaleLowerCase("tr");
                const bKey = (b.name || (b.email || "").split("@")[0] || "").toLocaleLowerCase("tr");
                return aKey.localeCompare(bKey, "tr");
              });
          },
          render: () => {
            return {
              onStart: (props) => {
                suggestionRef.current = props;
                setSuggestion({
                  items: props.items,
                  clientRect: props.clientRect,
                  index: 0,
                });
              },
              onUpdate: (props) => {
                suggestionRef.current = props;
                setSuggestion((prev) => ({
                  items: props.items,
                  clientRect: props.clientRect,
                  index: prev ? Math.min(prev.index, Math.max(0, props.items.length - 1)) : 0,
                }));
              },
              onKeyDown: ({ event }) => {
                const props = suggestionRef.current;
                if (!props || !props.items?.length) return false;
                if (event.key === "ArrowDown") {
                  setSuggestion((prev) => {
                    if (!prev) return prev;
                    return { ...prev, index: (prev.index + 1) % prev.items.length };
                  });
                  return true;
                }
                if (event.key === "ArrowUp") {
                  setSuggestion((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      index: (prev.index - 1 + prev.items.length) % prev.items.length,
                    };
                  });
                  return true;
                }
                if (event.key === "Enter" || event.key === "Tab") {
                  event.preventDefault();
                  // Read current index via a functional setState to avoid stale closure
                  let idx = 0;
                  setSuggestion((prev) => {
                    idx = prev?.index ?? 0;
                    return prev;
                  });
                  const user = props.items[idx];
                  pickMention(user);
                  return true;
                }
                if (event.key === "Escape") {
                  setSuggestion(null);
                  suggestionRef.current = null;
                  return true;
                }
                return false;
              },
              onExit: () => {
                setSuggestion(null);
                suggestionRef.current = null;
              },
            };
          },
        },
      }),
    [],
  );

  const suggestionOpenRef = useRef(false);
  useEffect(() => { suggestionOpenRef.current = !!(suggestion && suggestion.items.length > 0); }, [suggestion]);
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => { onSubmitRef.current = onSubmit; }, [onSubmit]);

  const editor = useEditor({
    autofocus: autoFocus ? "end" : false,
    editable: !readOnly,
    extensions: [
      StarterKit.configure({
        heading: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 underline underline-offset-2",
        },
      }),
      Placeholder.configure({ placeholder: placeholder || "" }),
      mentionExtension,
      Markdown.configure({
        html: false,
        tightLists: true,
        bulletListMarker: "-",
        linkify: false,
        breaks: true,
        transformPastedText: true,
      }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      const md = ed.storage.markdown.getMarkdown();
      onChange(md);
    },
    editorProps: {
      attributes: {
        class: [
          "prose prose-sm max-w-none min-h-[72px] px-3 py-2 outline-none",
          bordered
            ? readOnly
              ? "rounded-md border border-zinc-200 bg-zinc-50/50"
              : "rounded-b-md border border-t-0 border-zinc-200 bg-white"
            : "",
          "prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-blockquote:my-1",
          "prose-code:bg-zinc-100 prose-code:px-1 prose-code:rounded prose-code:before:content-none prose-code:after:content-none",
        ].join(" "),
        style: `min-height:${rows * 24}px`,
        "data-testid": testId || "richtext-editor",
      },
      // Slack-style Enter-to-submit for composers that pass `onSubmit`.
      // Rules:
      //   • Only trigger when a submit handler is provided (comments/description
      //     editors that omit `onSubmit` keep native Enter → newline behaviour).
      //   • Skip while the @mention picker is open (Mention extension handles it).
      //   • Skip inside list / blockquote / codeBlock so users can add lines
      //     inside multi-line structures.
      //   • Shift+Enter always inserts a soft newline.
      handleKeyDown: (view, event) => {
        if (event.key !== "Enter") return false;
        if (event.shiftKey) return false;
        if (!onSubmitRef.current) return false;
        if (suggestionOpenRef.current) return false;
        // If cursor is inside a structural block, keep native Enter behaviour
        // so users can e.g. add another bullet.
        const $from = view.state.selection.$from;
        for (let d = $from.depth; d > 0; d--) {
          const nodeType = $from.node(d).type.name;
          if (
            nodeType === "listItem" ||
            nodeType === "bulletList" ||
            nodeType === "orderedList" ||
            nodeType === "blockquote" ||
            nodeType === "codeBlock"
          ) {
            return false;
          }
        }
        event.preventDefault();
        onSubmitRef.current();
        return true;
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.storage.markdown.getMarkdown();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("Bağlantı URL'si:", previous);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertText = (text) => editor.chain().focus().insertContent(text).run();

  return (
    <div
      className={[
        "relative",
        bordered && !readOnly
          ? "rounded-md transition-shadow focus-within:ring-1 focus-within:ring-zinc-300"
          : "",
        className,
      ].join(" ")}
    >
      <Toolbar editor={editor} onLink={setLink} onInsertText={insertText} bordered={bordered} />
      <EditorContent editor={editor} />
      {suggestion && suggestion.items.length > 0 && (
        <MentionDropdown
          items={suggestion.items}
          activeIndex={suggestion.index}
          clientRect={suggestion.clientRect}
          onPick={pickMention}
          onHover={(i) =>
            setSuggestion((prev) => (prev ? { ...prev, index: i } : prev))
          }
        />
      )}
    </div>
  );
}

function MentionDropdown({ items, activeIndex, clientRect, onPick, onHover }) {
  const rect = clientRect?.();
  if (!rect) return null;
  const style = {
    position: "fixed",
    top: rect.bottom + 4,
    left: rect.left,
    zIndex: 60,
  };
  return (
    <ul
      data-testid="mention-dropdown"
      style={style}
      className="max-h-56 w-64 overflow-y-auto rounded-md border border-zinc-200 bg-white shadow-xl"
    >
      {items.map((u, i) => (
        <li key={u.id}>
          <button
            type="button"
            onMouseEnter={() => onHover(i)}
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(u);
            }}
            data-testid={`mention-pick-${u.id}`}
            className={[
              "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
              i === activeIndex ? "bg-zinc-100" : "hover:bg-zinc-50",
            ].join(" ")}
          >
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 text-[10px] font-semibold text-white">
              {nameInitials(u.name)}
            </div>
            <span className="flex-1 truncate">{u.name}</span>
            <span className="text-[10px] text-zinc-400">{u.email}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function TBtn({ active, onClick, title, testId, children }) {
  return (
    <button
      type="button"
      // Prevent focus loss from the editor so subsequent keystrokes stay in
      // the editor instead of bubbling to global keyboard shortcuts.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={testId}
      data-active={active ? "true" : "false"}
      className={[
        "rounded p-1.5 transition-colors",
        active
          ? "bg-zinc-900 text-white"
          : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor, onLink, onInsertText, bordered = true }) {
  return (
    <div
      data-testid="richtext-toolbar"
      className={[
        "flex flex-wrap items-center gap-0.5 px-2 py-1",
        bordered
          ? "rounded-t-md border border-b-0 border-zinc-200 bg-zinc-50/70"
          : "border-b border-zinc-100 bg-transparent",
      ].join(" ")}
    >
      <TBtn
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Kalın · Ctrl+B"
        testId="fmt-bold"
      >
        <Bold className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="İtalik · Ctrl+I"
        testId="fmt-italic"
      >
        <Italic className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        title="Altı çizili · Ctrl+U"
        testId="fmt-underline"
      >
        <UnderlineIcon className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        title="Üstü çizili"
        testId="fmt-strike"
      >
        <Strikethrough className="h-3.5 w-3.5" />
      </TBtn>
      <span className="mx-1 h-4 w-px bg-zinc-200" />
      <TBtn
        active={editor.isActive("link")}
        onClick={onLink}
        title="Bağlantı ekle · Ctrl+K"
        testId="fmt-link"
      >
        <LinkIcon className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Madde listesi"
        testId="fmt-ul"
      >
        <List className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Numaralı liste"
        testId="fmt-ol"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn
        active={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        title="Alıntı"
        testId="fmt-quote"
      >
        <Quote className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn
        active={editor.isActive("code")}
        onClick={() => editor.chain().focus().toggleCode().run()}
        title="Satır içi kod"
        testId="fmt-code"
      >
        <Code className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn
        active={editor.isActive("codeBlock")}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        title="Kod bloğu"
        testId="fmt-codeblock"
      >
        <Code2 className="h-3.5 w-3.5" />
      </TBtn>
      <span className="mx-1 h-4 w-px bg-zinc-200" />
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            title="Emoji ekle"
            data-testid="fmt-emoji"
            className="rounded p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
          >
            <Smile className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2">
          <div className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">
            Emoji
          </div>
          <div className="grid grid-cols-8 gap-1">
            {EMOJI_SET.map((emo) => (
              <button
                key={emo}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onInsertText(emo)}
                data-testid={`emoji-${emo}`}
                className="rounded p-1 text-base hover:bg-zinc-100"
              >
                {emo}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <TBtn
        active={false}
        onClick={() => onInsertText("@")}
        title="@ Kişi etiketle"
        testId="fmt-mention"
      >
        <AtSign className="h-3.5 w-3.5" />
      </TBtn>
    </div>
  );
}
